import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  KanbanSquare,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  LayoutGrid,
  Table2,
  AlertTriangle,
  Pencil,
} from 'lucide-react';
import clsx from 'clsx';
import { useDeals, useSaveDeal, useMoveDeal, useCloseDeal } from '../api/deals';
import { useProperties } from '../api/properties';
import { useClients } from '../api/clients';
import {
  ETAPAS_POR_TIPO,
  ETAPA_LABEL,
  ETAPA_STYLE,
  TIPO_DEAL_LABEL,
  CONTRATOS,
  CONTRATO_LABEL,
  type Contrato,
  type Deal,
  type EtapaDeal,
  type TipoDeal,
} from '../lib/types';
import { AyudaGuia } from '../components/Ayuda';

const eur = (n?: number) => (n ? `${n.toLocaleString('es-ES')} €` : '—');

// Días desde que la operación entró en su etapa actual. Sale de la última
// marca de historial_etapas; si falta, del alta. Sirve para ver lo estancado.
function diasEnEtapa(d: Deal): number {
  const ts =
    d.historial_etapas?.[d.historial_etapas.length - 1]?.ts ?? d.created_at ?? 0;
  if (!ts) return 0;
  return Math.floor((Date.now() / 1000 - ts) / 86400);
}

export default function OperacionesView() {
  const [tipo, setTipo] = useState<TipoDeal>('comercializacion');
  const [vista, setVista] = useState<'kanban' | 'tabla'>('kanban');
  const [creating, setCreating] = useState(false);
  // Editar una operación abierta: el importe o la vivienda mal puestos se
  // corrigen antes del cierre, que ya no se deshace.
  const [editando, setEditando] = useState<Deal | null>(null);
  const [closing, setClosing] = useState<Deal | null>(null);

  const { data: deals, isLoading, isError, error, refetch } = useDeals(tipo);
  const { data: propsList } = useProperties();
  const { data: clientsList } = useClients();

  // Mapas para pintar el código de vivienda y el nombre de cliente en la tarjeta
  // sin una llamada por operación.
  const propByeId = useMemo(
    () => Object.fromEntries((propsList ?? []).map((p) => [p.property_id, p])),
    [propsList],
  );
  const cliById = useMemo(
    () => Object.fromEntries((clientsList ?? []).map((c) => [c.client_id, c])),
    [clientsList],
  );

  const etapas = ETAPAS_POR_TIPO[tipo];
  const activos = (deals ?? []).filter((d) => !d.commission_id);
  const kpis = {
    activas: activos.length,
    cerradas: (deals ?? []).filter((d) => d.commission_id).length,
    pipeline: activos.reduce((s, d) => s + (d.importe ?? 0), 0),
  };

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight text-deep">Operaciones</h1>
          <AyudaGuia seccion="operaciones" />
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition hover:bg-deep"
        >
          <Plus size={16} /> Nueva operación
        </button>
      </div>

      {/* Selector de tablero + selector de vista */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full bg-soft p-1">
          {(Object.keys(ETAPAS_POR_TIPO) as TipoDeal[]).map((t) => (
            <button
              key={t}
              onClick={() => setTipo(t)}
              className={clsx(
                'rounded-full px-4 py-1.5 text-sm font-bold transition',
                tipo === t ? 'bg-brand text-white' : 'text-ink/55 hover:text-deep',
              )}
            >
              {TIPO_DEAL_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full border border-deep/10 bg-surface p-1">
          <ToggleVista activa={vista === 'kanban'} onClick={() => setVista('kanban')} icon={LayoutGrid} label="Tablero" />
          <ToggleVista activa={vista === 'tabla'} onClick={() => setVista('tabla')} icon={Table2} label="Tabla" />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <Kpi label="Activas" value={String(kpis.activas)} />
        <Kpi label="Cerradas" value={String(kpis.cerradas)} />
        <Kpi label="Importe en marcha" value={eur(kpis.pipeline)} />
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError ? (
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar las operaciones</p>
          <p className="mt-1 text-sm text-red-600/80">
            Es un fallo del servidor, no es que no haya ninguna. {(error as Error)?.message}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-red-600 px-5 py-2 text-sm font-bold text-white transition hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      ) : !deals?.length ? (
        <div className="mt-8 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-14 text-center">
          <KanbanSquare className="mx-auto text-brand" size={28} strokeWidth={1.5} />
          <p className="mt-3 text-sm text-ink/60">
            Aún no hay operaciones de {TIPO_DEAL_LABEL[tipo].toLowerCase()}. Crea la primera con
            «Nueva operación».
          </p>
        </div>
      ) : vista === 'kanban' ? (
        <Kanban
          tipo={tipo}
          etapas={etapas}
          deals={deals}
          propByeId={propByeId}
          cliById={cliById}
          onClose={setClosing}
          onEdit={setEditando}
        />
      ) : (
        <Tabla
          tipo={tipo}
          etapas={etapas}
          deals={deals}
          propByeId={propByeId}
          cliById={cliById}
          onClose={setClosing}
          onEdit={setEditando}
        />
      )}

      {creating && (
        <Modal title="Nueva operación" onClose={() => setCreating(false)}>
          <DealForm tipoInicial={tipo} onDone={() => setCreating(false)} />
        </Modal>
      )}
      {editando && (
        <Modal title="Editar operación" onClose={() => setEditando(null)}>
          <DealForm tipoInicial={editando.tipo} deal={editando} onDone={() => setEditando(null)} />
        </Modal>
      )}
      {closing && (
        <CloseModal
          deal={closing}
          prop={closing.property_id ? propByeId[closing.property_id] : undefined}
          onClose={() => setClosing(null)}
        />
      )}
    </div>
  );
}

function ToggleVista({
  activa,
  onClick,
  icon: Icon,
  label,
}: {
  activa: boolean;
  onClick: () => void;
  icon: typeof LayoutGrid;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition',
        activa ? 'bg-deep text-white' : 'text-ink/50 hover:text-deep',
      )}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-surface border border-deep/10 px-4 py-3">
      <p className="text-2xl font-extrabold text-deep">{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
    </div>
  );
}

type Lookups = {
  propByeId: Record<string, { codigo: string; direccion: string }>;
  cliById: Record<string, { nombre: string }>;
};

// ── Tablero kanban ────────────────────────────────────────────────
// Columnas = etapas del tipo. En móvil hacen scroll horizontal; el movimiento
// NO es drag&drop (mala experiencia a 390px), sino ◀/▶ en cada tarjeta: mismo
// gesto en escritorio y móvil.
function Kanban({
  tipo,
  etapas,
  deals,
  propByeId,
  cliById,
  onClose,
  onEdit,
}: {
  tipo: TipoDeal;
  etapas: EtapaDeal[];
  deals: Deal[];
  onClose: (d: Deal) => void;
  onEdit: (d: Deal) => void;
} & Lookups) {
  const porEtapa = (e: EtapaDeal) => deals.filter((d) => d.etapa === e);
  return (
    <div className="mt-6 flex snap-x gap-4 overflow-x-auto pb-2 sm:grid" style={{ gridTemplateColumns: `repeat(${etapas.length}, minmax(0, 1fr))` }}>
      {etapas.map((e) => {
        const cards = porEtapa(e);
        return (
          <div key={e} className="w-[78vw] shrink-0 snap-start sm:w-auto">
            <div className="mb-3 flex items-center justify-between px-1">
              <span className={clsx('rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider', ETAPA_STYLE[e])}>
                {ETAPA_LABEL[e]}
              </span>
              <span className="text-xs font-bold text-ink/40">{cards.length}</span>
            </div>
            <div className="flex flex-col gap-3">
              {cards.map((d) => (
                <DealCard
                  key={d.deal_id}
                  d={d}
                  tipo={tipo}
                  etapas={etapas}
                  propByeId={propByeId}
                  cliById={cliById}
                  onClose={onClose}
                  onEdit={onEdit}
                />
              ))}
              {!cards.length && (
                <div className="rounded-xl border border-dashed border-deep/10 py-6 text-center text-xs text-ink/30">
                  Vacío
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DealCard({
  d,
  tipo,
  etapas,
  propByeId,
  cliById,
  onClose,
  onEdit,
}: {
  d: Deal;
  tipo: TipoDeal;
  etapas: EtapaDeal[];
  onClose: (d: Deal) => void;
  onEdit: (d: Deal) => void;
} & Lookups) {
  const move = useMoveDeal(tipo);
  const idx = etapas.indexOf(d.etapa);
  const esUltima = idx === etapas.length - 1;
  const cerrada = Boolean(d.commission_id);
  const prop = d.property_id ? propByeId[d.property_id] : undefined;
  const cli = d.client_id ? cliById[d.client_id] : undefined;
  const dias = diasEnEtapa(d);

  const irA = (delta: number) => {
    const dest = etapas[idx + delta];
    if (dest && !move.isPending) move.mutate({ dealId: d.deal_id, etapa: dest });
  };

  return (
    <div className="rounded-2xl border border-deep/10 bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold text-deep">{d.titulo || 'Sin título'}</p>
        {!cerrada && <BotonEditar onClick={() => onEdit(d)} />}
      </div>
      <p className="mt-0.5 text-xs text-ink/50">
        {prop ? `${prop.codigo} · ${prop.direccion}` : 'Sin vivienda'}
      </p>
      {cli && <p className="text-xs text-ink/50">{cli.nombre}</p>}
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="font-bold text-brand">{eur(d.importe)}</span>
        <span className={clsx(dias >= 14 ? 'text-amber-600' : 'text-ink/40')}>
          {dias === 0 ? 'hoy' : `${dias} d`}
        </span>
      </div>

      {cerrada ? (
        <div className="mt-3 flex items-center gap-1.5 rounded-full bg-brand/15 px-3 py-1.5 text-xs font-bold text-brand">
          <CheckCircle2 size={13} /> Cerrada · {d.estado_cierre}
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => irA(-1)}
            disabled={idx === 0 || move.isPending}
            aria-label="Etapa anterior"
            className="rounded-full border border-deep/15 p-1.5 text-deep transition hover:bg-soft disabled:opacity-30"
          >
            <ChevronLeft size={15} />
          </button>
          <span className="flex-1 truncate text-center text-xs font-semibold text-ink/60">
            {ETAPA_LABEL[d.etapa]}
          </span>
          {esUltima && tipo === 'comercializacion' ? (
            <button
              onClick={() => onClose(d)}
              className="rounded-full bg-deep px-3 py-1.5 text-xs font-bold text-white transition hover:bg-brand"
            >
              Cerrar
            </button>
          ) : (
            <button
              onClick={() => irA(1)}
              disabled={esUltima || move.isPending}
              aria-label="Etapa siguiente"
              className="rounded-full border border-deep/15 p-1.5 text-deep transition hover:bg-soft disabled:opacity-30"
            >
              <ChevronRight size={15} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Vista tabla ───────────────────────────────────────────────────
function Tabla({
  tipo,
  etapas,
  deals,
  propByeId,
  cliById,
  onClose,
  onEdit,
}: {
  tipo: TipoDeal;
  etapas: EtapaDeal[];
  deals: Deal[];
  onClose: (d: Deal) => void;
  onEdit: (d: Deal) => void;
} & Lookups) {
  const move = useMoveDeal(tipo);
  return (
    <div className="mt-6 overflow-x-auto rounded-2xl border border-deep/10 bg-surface">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-deep/10 text-left text-[0.68rem] uppercase tracking-wider text-ink/45">
            <th className="px-4 py-3 font-bold">Operación</th>
            <th className="px-4 py-3 font-bold">Vivienda</th>
            <th className="px-4 py-3 font-bold">Cliente</th>
            <th className="px-4 py-3 font-bold">Importe</th>
            <th className="px-4 py-3 font-bold">Etapa</th>
            <th className="px-4 py-3 font-bold"></th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => {
            const idx = etapas.indexOf(d.etapa);
            const esUltima = idx === etapas.length - 1;
            const cerrada = Boolean(d.commission_id);
            const prop = d.property_id ? propByeId[d.property_id] : undefined;
            const cli = d.client_id ? cliById[d.client_id] : undefined;
            const irA = (delta: number) => {
              const dest = etapas[idx + delta];
              if (dest && !move.isPending) move.mutate({ dealId: d.deal_id, etapa: dest });
            };
            return (
              <tr key={d.deal_id} className="border-b border-deep/5 last:border-0">
                <td className="px-4 py-3 font-semibold text-deep">
                  <span className="inline-flex items-center gap-1.5">
                    {d.titulo || 'Sin título'}
                    {!d.commission_id && <BotonEditar onClick={() => onEdit(d)} />}
                  </span>
                </td>
                <td className="px-4 py-3 text-ink/60">{prop ? prop.codigo : '—'}</td>
                <td className="px-4 py-3 text-ink/60">{cli ? cli.nombre : '—'}</td>
                <td className="px-4 py-3 font-bold text-brand">{eur(d.importe)}</td>
                <td className="px-4 py-3">
                  <span className={clsx('rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider', ETAPA_STYLE[d.etapa])}>
                    {ETAPA_LABEL[d.etapa]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {cerrada ? (
                    <span className="flex items-center gap-1 text-xs font-bold text-brand">
                      <CheckCircle2 size={13} /> {d.estado_cierre}
                    </span>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => irA(-1)}
                        disabled={idx === 0 || move.isPending}
                        aria-label="Etapa anterior"
                        className="rounded-full border border-deep/15 p-1 text-deep transition hover:bg-soft disabled:opacity-30"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      {esUltima && tipo === 'comercializacion' ? (
                        <button
                          onClick={() => onClose(d)}
                          className="rounded-full bg-deep px-3 py-1 text-xs font-bold text-white transition hover:bg-brand"
                        >
                          Cerrar
                        </button>
                      ) : (
                        <button
                          onClick={() => irA(1)}
                          disabled={esUltima || move.isPending}
                          aria-label="Etapa siguiente"
                          className="rounded-full border border-deep/15 p-1 text-deep transition hover:bg-soft disabled:opacity-30"
                        >
                          <ChevronRight size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Alta de operación ─────────────────────────────────────────────
type DealFormData = {
  tipo: TipoDeal;
  titulo: string;
  property_id: string;
  client_id: string;
  operacion: 'venta' | 'alquiler';
  contrato: Contrato;
  importe: number;
  notas: string;
};

const field =
  'mt-1 w-full rounded-xl border border-deep/15 bg-soft px-3.5 py-2.5 text-base outline-none focus:border-brand transition';
const label = 'block text-xs font-bold uppercase tracking-wider text-ink/50';

function DealForm({
  tipoInicial,
  deal,
  onDone,
}: {
  tipoInicial: TipoDeal;
  deal?: Deal;
  onDone: () => void;
}) {
  const save = useSaveDeal();
  const { data: propsList } = useProperties();
  const { data: clientsList } = useClients();
  const { register, handleSubmit, watch, formState } = useForm<DealFormData>({
    defaultValues: {
      tipo: deal?.tipo ?? tipoInicial,
      operacion: deal?.operacion ?? 'venta',
      contrato: deal?.contrato ?? 'venta',
      titulo: deal?.titulo ?? '',
      property_id: deal?.property_id ?? '',
      client_id: deal?.client_id ?? '',
      importe: deal?.importe ?? 0,
      notas: deal?.notas ?? '',
    },
  });
  const tipo = watch('tipo');

  const onSubmit = handleSubmit(async (data) => {
    // operacion se deriva del contrato (venta → venta; temporada/larga → alquiler)
    // para que el dato quede coherente sin un segundo selector.
    const operacion = data.contrato === 'venta' ? 'venta' : 'alquiler';
    // Al editar se manda la etapa en la que está: sin ella, el servidor la
    // devolvería a la primera.
    await save.mutateAsync(
      deal ? { ...data, operacion, deal_id: deal.deal_id, etapa: deal.etapa } : { ...data, operacion },
    );
    onDone();
  });

  return (
    <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
      <label className="block">
        <span className={label}>Tipo de operación</span>
        {/* El tipo no cambia una vez creada: cambiaría sus etapas. */}
        <select {...register('tipo')} disabled={Boolean(deal)} className={field}>
          <option value="comercializacion">Comercialización</option>
          <option value="captacion">Captación</option>
        </select>
      </label>
      {tipo === 'comercializacion' && (
        <label className="block">
          <span className={label}>Contrato</span>
          <select {...register('contrato')} className={field}>
            {CONTRATOS.map((c) => (
              <option key={c} value={c}>{CONTRATO_LABEL[c]}</option>
            ))}
          </select>
        </label>
      )}
      <label className="block sm:col-span-2">
        <span className={label}>Título *</span>
        <input {...register('titulo', { required: true })} className={field} placeholder="Venta Serrano 21 · familia López" />
      </label>
      <label className="block">
        <span className={label}>Vivienda</span>
        <select {...register('property_id')} className={field}>
          <option value="">— Sin vincular —</option>
          {(propsList ?? [])
            .filter((p) => p.property_id !== '_counter')
            .map((p) => (
              <option key={p.property_id} value={p.property_id}>
                {p.codigo} · {p.direccion}
              </option>
            ))}
        </select>
      </label>
      <label className="block">
        <span className={label}>Cliente</span>
        <select {...register('client_id')} className={field}>
          <option value="">— Sin vincular —</option>
          {(clientsList ?? []).map((c) => (
            <option key={c.client_id} value={c.client_id}>
              {c.nombre}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className={label}>Importe operación €</span>
        <input type="number" {...register('importe', { valueAsNumber: true })} className={field} />
      </label>
      <label className="block sm:col-span-2">
        <span className={label}>Notas</span>
        <textarea rows={2} {...register('notas')} className={`${field} resize-none`} />
      </label>
      <div className="sm:col-span-2 flex items-center gap-3">
        <button
          type="submit"
          disabled={formState.isSubmitting}
          className="rounded-full bg-brand px-7 py-2.5 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
        >
          {deal ? 'Guardar cambios' : 'Crear operación'}
        </button>
        {save.isError && <span className="text-sm text-red-700">No se pudo guardar. Reintenta.</span>}
      </div>
    </form>
  );
}

// ── Cierre → comisión ─────────────────────────────────────────────
// La comisión sugerida depende del CONTRATO, que es lo que decide cómo se cobra:
//   venta         → 3% del importe de la operación
//   larga estancia→ una mensualidad (el precio de alquiler de la vivienda, LAU)
//   temporada     → 0 sugerido, porque no se le factura al propietario (lo paga
//                   el inquilino); el agente introduce lo que cobra la agencia
// Siempre editable: el % real cambia por acuerdo y no vive en el CRM.
function CloseModal({
  deal,
  prop,
  onClose,
}: {
  deal: Deal;
  prop?: { codigo: string; direccion: string; precio_alquiler?: number };
  onClose: () => void;
}) {
  const cerrar = useCloseDeal();
  const contrato = deal.contrato ?? 'venta';
  const [tipoCierre, setTipoCierre] = useState<'venta' | 'alquiler'>(
    contrato === 'venta' ? 'venta' : 'alquiler',
  );
  const sugerida =
    contrato === 'venta'
      ? Math.round((deal.importe ?? 0) * 0.03)
      : contrato === 'larga_estancia'
        ? prop?.precio_alquiler ?? 0
        : 0;
  const notaComision =
    contrato === 'venta'
      ? 'Sugerido 3% · editable'
      : contrato === 'larga_estancia'
        ? 'Sugerido: una mensualidad (LAU) · editable'
        : 'Temporada: la paga el inquilino. Introduce lo que cobra la agencia.';
  const [comision, setComision] = useState<number>(sugerida);
  const [flip, setFlip] = useState<boolean>(Boolean(deal.property_id));

  const submit = async () => {
    await cerrar.mutateAsync({
      dealId: deal.deal_id,
      importe_comision: comision,
      estado_cierre: tipoCierre,
      flip_vivienda: flip,
    });
    onClose();
  };

  const estadoResultante = tipoCierre === 'venta' ? 'vendida' : 'alquilada';

  return (
    <Modal title={`Cerrar · ${deal.titulo || 'operación'}`} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink/60">
          <span>{prop ? `${prop.codigo} · ${prop.direccion}` : 'Operación sin vivienda vinculada'}</span>
          <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-[0.68rem] font-bold uppercase tracking-wider text-brand">
            {CONTRATO_LABEL[contrato]}
          </span>
        </div>

        <div>
          <span className={label}>Tipo de cierre</span>
          <div className="mt-2 inline-flex rounded-full bg-soft p-1">
            {(['venta', 'alquiler'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTipoCierre(t)}
                className={clsx(
                  'rounded-full px-5 py-1.5 text-sm font-bold capitalize transition',
                  tipoCierre === t ? 'bg-brand text-white' : 'text-ink/55 hover:text-deep',
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className={label}>Importe operación</span>
            <p className="mt-1 rounded-xl bg-soft px-3.5 py-2.5 font-bold text-deep">{eur(deal.importe)}</p>
          </label>
          <label className="block">
            <span className={label}>Comisión €</span>
            <input
              type="number"
              value={comision}
              onChange={(e) => setComision(Number(e.target.value))}
              className={field}
            />
            <span className="mt-1 block text-[0.7rem] text-ink/40">{notaComision}</span>
          </label>
        </div>

        {deal.property_id && (
          <label className="flex items-center gap-2 text-sm text-ink/70">
            <input
              type="checkbox"
              checked={flip}
              onChange={(e) => setFlip(e.target.checked)}
              className="h-4 w-4 accent-brand"
            />
            Marcar la vivienda como «{estadoResultante}»
          </label>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={submit}
            disabled={cerrar.isPending || comision <= 0}
            className="rounded-full bg-deep px-7 py-2.5 text-sm font-bold text-white transition hover:bg-brand disabled:opacity-50"
          >
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar y crear comisión'}
          </button>
          {cerrar.isError && (
            <span className="text-sm text-red-700">
              {(cerrar.error as Error)?.message ?? 'No se pudo cerrar. Reintenta.'}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-6" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 sm:max-w-xl sm:rounded-3xl sm:p-7"
      >
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="truncate text-lg font-extrabold text-deep">{title}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-ink/50 hover:bg-soft">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BotonEditar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Editar operación"
      title="Editar operación"
      className="shrink-0 rounded-full p-1 text-ink/35 transition hover:bg-soft hover:text-brand"
    >
      <Pencil size={14} />
    </button>
  );
}
